import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "./api";

// ---- Users (admin-only management) -----------------------------------------

export interface PanelUser {
  id: number;
  tenant_id: string;
  email: string;
  full_name: string | null;
  role: "admin" | "operator" | "viewer";
  is_active: boolean;
  created_at: string;
  last_login_at: string | null;
}

export interface UserList {
  items: PanelUser[];
  total: number;
}

export function useUsers(enabled = true) {
  return useQuery({
    queryKey: ["users"],
    queryFn: ({ signal }) => apiGet<UserList>("/users", signal),
    enabled,
  });
}

export interface CreateUserBody {
  email: string;
  password: string;
  full_name?: string | null;
  role?: "admin" | "operator" | "viewer";
  tenant_id?: string;
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateUserBody) => apiPost<PanelUser>("/users", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export interface UpdateUserBody {
  full_name?: string | null;
  role?: "admin" | "operator" | "viewer";
  is_active?: boolean;
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: UpdateUserBody }) => {
      const r = await fetch(`/api/users/${id}`, {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(`PATCH /users/${id} failed: ${r.status}`);
      return r.json() as Promise<PanelUser>;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export function useResetPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, new_password }: { id: number; new_password: string }) =>
      apiPost<{ ok: string; email: string }>(`/users/${id}/password`, { new_password }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export function useDeactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const r = await fetch(`/api/users/${id}`, {
        method: "DELETE",
        credentials: "same-origin",
      });
      if (!r.ok) throw new Error(`DELETE /users/${id} failed: ${r.status}`);
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

// ---- Dashboard KPIs --------------------------------------------------------

export interface DashboardKpis {
  audits_24h:    number;
  findings_open: number;
  reports_30d:   number;
}

export function useDashboardKpis() {
  return useQuery({
    queryKey: ["dashboard", "kpis"],
    queryFn: ({ signal }) => apiGet<DashboardKpis>("/dashboard/kpis", signal),
    refetchInterval: 60_000,
  });
}

// ---- Reports (customer-facing PDFs) ---------------------------------------

export type ReportKind = "crawl" | "traffic" | "backlinks" | "seo_audit";

export interface Report {
  id: number;
  tenant_id: string;
  audit_id: number | null;
  report_kind: ReportKind;
  crawl_id: number | null;
  property_id: number | null;
  title: string;
  customer: string;
  period: string | null;
  file_size_bytes: number | null;
  created_at: string;
  created_by_user_id: number | null;
  created_by_email: string | null;
}

export interface ReportList {
  items: Report[];
  total: number;
  limit: number;
  offset: number;
}

export interface ReportListFilter {
  limit?: number;
  offset?: number;
  kind?: ReportKind | null;
  property_id?: number | null;
  crawl_id?: number | null;
  /**
   * Tighter polling interval — used by the Generate Report flow on a
   * per-page widget so the new row appears within ~10s of the script
   * finishing. Default 60s for the global Reports tab.
   */
  refetchMs?: number;
}

export function useReports(arg?: number | ReportListFilter, offsetMaybe = 0) {
  // Back-compat: useReports(50) still works for callers that just want
  // the global list. The filter form is the new code path.
  const filter: ReportListFilter =
    typeof arg === "number" || arg === undefined
      ? { limit: arg ?? 50, offset: offsetMaybe }
      : arg;
  const limit = filter.limit ?? 50;
  const offset = filter.offset ?? 0;
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  if (filter.kind) params.set("kind", filter.kind);
  if (filter.property_id != null)
    params.set("property_id", String(filter.property_id));
  if (filter.crawl_id != null) params.set("crawl_id", String(filter.crawl_id));
  return useQuery({
    queryKey: ["reports", { limit, offset, kind: filter.kind ?? null,
                            property_id: filter.property_id ?? null,
                            crawl_id: filter.crawl_id ?? null }],
    queryFn: ({ signal }) =>
      apiGet<ReportList>(`/reports?${params.toString()}`, signal),
    refetchInterval: filter.refetchMs ?? 60_000,
  });
}

export function useReport(id: number | null) {
  return useQuery({
    queryKey: ["reports", id],
    queryFn: ({ signal }) => apiGet<Report>(`/reports/${id}`, signal),
    enabled: id !== null,
  });
}

export function reportDownloadUrl(id: number): string {
  return `/api/reports/${id}/download`;
}

// ---- Generate Report (background subprocess kicked off server-side) -------
//
// The POST endpoints fire-and-forget: they spawn make_report.py + return
// `{started: true}` immediately. The script writes its `reports` row when
// done, so the SPA polls `useReports({kind, ...})` to detect completion
// (typically 1-3 minutes per the reportify Claude pass).

export interface GenerateReportResponse {
  ok: boolean;
  started: boolean;
  kind: ReportKind;
  crawl_id?: number;
  property_id?: number;
  title: string;
  customer: string;
  message: string;
}

export function useGenerateCrawlReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { crawl_id: number; title?: string; customer?: string }) => {
      const params = new URLSearchParams();
      if (vars.title) params.set("title", vars.title);
      if (vars.customer) params.set("customer", vars.customer);
      const qs = params.toString() ? `?${params.toString()}` : "";
      return apiPost<GenerateReportResponse>(
        `/crawls/${vars.crawl_id}/reports${qs}`,
        {},
      );
    },
    onSuccess: () => {
      // The new row will arrive ~1-3 min later; speed up the next refetch.
      qc.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

export function useGeneratePropertyReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      property_id: number;
      kind: "traffic" | "backlinks";
      title?: string;
      customer?: string;
      period?: string;
    }) => {
      const params = new URLSearchParams();
      params.set("kind", vars.kind);
      if (vars.title) params.set("title", vars.title);
      if (vars.customer) params.set("customer", vars.customer);
      if (vars.period) params.set("period", vars.period);
      return apiPost<GenerateReportResponse>(
        `/properties/${vars.property_id}/reports?${params.toString()}`,
        {},
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

// ---- Tenants (admin-only) -------------------------------------------------

export type TenantStatus = "active" | "suspended" | "archived";

export interface Tenant {
  id: string;
  display_name: string;
  status: TenantStatus;
  has_telegram_chat: boolean;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface TenantList {
  items: Tenant[];
  total: number;
}

export function useTenants(enabled = true) {
  return useQuery({
    queryKey: ["tenants"],
    queryFn: ({ signal }) => apiGet<TenantList>("/tenants", signal),
    enabled,
    staleTime: 60_000,
  });
}

export interface CreateTenantBody {
  id: string;
  display_name: string;
  telegram_chat_id?: string | null;
}

export function useCreateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTenantBody) =>
      apiPost<Tenant, CreateTenantBody>("/tenants", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenants"] }),
  });
}

export interface UpdateTenantBody {
  display_name?: string;
  status?: TenantStatus;
  telegram_chat_id?: string | null;
}

export function useUpdateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: UpdateTenantBody }) => {
      const r = await fetch(`/api/tenants/${id}`, {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(`PATCH /tenants/${id} failed: ${r.status}`);
      return r.json() as Promise<Tenant>;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenants"] }),
  });
}

// ---- Secrets (tenant secrets store) ---------------------------------------

export interface TenantSecret {
  id: number;
  name: string;
  description: string | null;
  created_at: string;
  rotated_at: string | null;
}

export interface SecretsList {
  items: TenantSecret[];
  total: number;
}

export function useSecrets(enabled = true) {
  return useQuery({
    queryKey: ["secrets"],
    queryFn: ({ signal }) => apiGet<SecretsList>("/secrets", signal),
    enabled,
    staleTime: 60_000,
  });
}

export interface UpsertSecretBody {
  name: string;
  value: string;
  description?: string | null;
}

export function useUpsertSecret() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertSecretBody) =>
      apiPost<{ ok: boolean; id: number }, UpsertSecretBody>("/secrets", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["secrets"] }),
  });
}

export function useDeleteSecret() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      fetch(`/api/secrets/${id}`, {
        method: "DELETE", credentials: "same-origin",
      }).then(async (r) => {
        if (!r.ok) throw new Error(`DELETE failed: ${r.status}`);
        return r.json();
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["secrets"] }),
  });
}

// ---- Properties (per-tenant websites/brands under audit) -----------------

export interface Property {
  id: number;
  tenant_id: string;
  domain: string;
  display_name: string;
  ahrefs_project_id: string | null;
  semrush_project_id: string | null;
  gsc_property_id: string | null;
  ga4_property_id: string | null;
  google_ads_customer_id: string | null;
  // SSH server access — read-only here. Mutated via /ssh/* endpoints,
  // not via /properties PATCH.
  ssh_host: string | null;
  ssh_user: string | null;
  ssh_port: number | null;
  ssh_connected_at: string | null;
  ssh_last_test_ok: boolean | null;
  target_keywords: string[];
  competitor_domains: string[];
  brand_terms: string[];
  audit_cron: string;
  // Scheduled Frog crawls (migration 0009). null = manual only.
  crawl_schedule: string | null;
  last_scheduled_crawl_at: string | null;
  crawl_schedule_max_urls: number;
  // Ahrefs Brand Radar Report ID (migration 0010). null = no AI tracking.
  brand_radar_report_id: string | null;
  // Ahrefs site-explorer target override (migration 0011). null = use domain.
  ahrefs_target_override: string | null;
  // Ahrefs site-explorer mode (migration 0007). null = subdomains (default).
  ahrefs_mode?: string | null;
  // Google Business Profile listing name (migration 0016). null = no GBP
  // presence; the DFS bulk pull skips google_reviews + gbp_my_business_info.
  gbp_listing_name: string | null;
  // Crawl throttle defaults (migrations 0018 / 0019)
  crawl_max_threads: number;
  crawl_max_url_per_sec: number;
  // Crawl configurator (migration 0022 — Phase 1 of the gregorco fix).
  // Per-property defaults. Per-crawl overrides live on the crawls row.
  exclude_url_patterns: string[];
  include_url_patterns: string[];
  strip_query_params: string[];
  max_crawl_depth: number;  // 0 = unlimited
  is_active: boolean;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface PropertyList {
  items: Property[];
  total: number;
}

export function useProperties(arg: boolean | { include_inactive?: boolean } = false) {
  // Backwards-compat: previously a bare boolean. Object form is the
  // preferred shape going forward (room to add filters without
  // breaking call sites).
  const includeInactive = typeof arg === "boolean" ? arg : !!arg.include_inactive;
  const qs = includeInactive ? "?include_inactive=true" : "";
  return useQuery({
    queryKey: ["properties", { includeInactive }],
    queryFn: ({ signal }) => apiGet<PropertyList>(`/properties${qs}`, signal),
    staleTime: 30_000,
  });
}

export function useProperty(id: number | null) {
  return useQuery({
    queryKey: ["properties", id],
    queryFn: ({ signal }) => apiGet<Property>(`/properties/${id}`, signal),
    enabled: id !== null,
  });
}

export interface CreatePropertyBody {
  domain: string;
  display_name: string;
  ahrefs_project_id?: string | null;
  semrush_project_id?: string | null;
  gsc_property_id?: string | null;
  ga4_property_id?: string | null;
  google_ads_customer_id?: string | null;
  // cpanel_host / cpanel_user removed 2026-05-11 — server SSH access
  // is now self-serve per property via the SSH connect strip.
  target_keywords?: string[];
  competitor_domains?: string[];
  brand_terms?: string[];
  audit_cron?: string;
  gbp_listing_name?: string | null;
}

export function useCreateProperty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePropertyBody) =>
      apiPost<Property, CreatePropertyBody>("/properties", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["properties"] }),
  });
}

export interface UpdatePropertyBody {
  display_name?: string;
  ahrefs_project_id?: string | null;
  semrush_project_id?: string | null;
  gsc_property_id?: string | null;
  ga4_property_id?: string | null;
  google_ads_customer_id?: string | null;
  // cpanel_* removed — see CreatePropertyBody note.
  target_keywords?: string[];
  competitor_domains?: string[];
  brand_terms?: string[];
  audit_cron?: string;
  crawl_schedule?: string | null;          // "" or null to disable
  crawl_schedule_max_urls?: number;
  brand_radar_report_id?: string | null;   // "" or null to disable
  ahrefs_target_override?: string | null;  // "" or null to clear
  ahrefs_mode?: "subdomains" | "domain" | "exact";
  gbp_listing_name?: string | null;        // "" or null to clear
  // Crawl configurator (migration 0022)
  exclude_url_patterns?: string[];
  include_url_patterns?: string[];
  strip_query_params?: string[];
  max_crawl_depth?: number;  // 0 = unlimited
  is_active?: boolean;
}

export function useUpdateProperty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: UpdatePropertyBody }) => {
      const r = await fetch(`/api/properties/${id}`, {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(`PATCH /properties/${id} failed: ${r.status}`);
      return r.json() as Promise<Property>;
    },
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: ["properties"] });
      qc.invalidateQueries({ queryKey: ["properties", id] });
    },
  });
}

export function useDeactivateProperty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const r = await fetch(`/api/properties/${id}`, {
        method: "DELETE",
        credentials: "same-origin",
      });
      if (!r.ok) throw new Error(`DELETE /properties/${id} failed: ${r.status}`);
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["properties"] }),
  });
}

// ---- Audits (per-property runs) -------------------------------------------

export type Severity = "critical" | "high" | "medium" | "low" | "info";
export type FindingStatus = "open" | "acknowledged" | "resolved" | "snoozed";

export type AuditStatus = "running" | "ok" | "error";

export interface Audit {
  id: number;
  tenant_id?: string;
  kind: string;
  display_prompt?: string | null;
  prompt?: string | null;
  output: string;
  duration_s: number | null;
  ok: boolean;
  error: string | null;
  metadata: Record<string, unknown>;
  run_at: string;
  /** Derived server-side. Single source of truth — do NOT recompute
   * from output/error/ok in the UI; the rules are subtle. */
  status: AuditStatus;
}

export interface AuditDetail extends Audit {
  findings: Finding[];
}

export interface AuditList { items: Audit[]; total: number; }

export interface Finding {
  id: number;
  tenant_id?: string;
  audit_id: number | null;
  kind: string;
  severity: Severity;
  status: FindingStatus;
  title: string;
  detail: string | null;
  metadata: Record<string, unknown>;
  detected_at: string;
  resolved_at: string | null;
  audit_kind?: string | null;
  /** Real FK to properties.id (since migration 0003). Nullable for
   * historical findings or chat audits not bound to a property. */
  property_id: number | null;
  property_domain?: string | null;
  property_active?: boolean | null;
}

export interface FindingList { items: Finding[]; total: number; }

interface AuditsArgs {
  property_id?: number;
  kind?: string;
  limit?: number;
  offset?: number;
}

export function useAudits(args: AuditsArgs = {}) {
  const qs = new URLSearchParams();
  if (args.property_id !== undefined) qs.set("property_id", String(args.property_id));
  if (args.kind) qs.set("kind", args.kind);
  qs.set("limit",  String(args.limit  ?? 50));
  qs.set("offset", String(args.offset ?? 0));
  return useQuery({
    queryKey: ["audits", args],
    queryFn: ({ signal }) => apiGet<AuditList>(`/audits?${qs.toString()}`, signal),
    refetchInterval: 30_000,
  });
}

export function useAudit(id: number | null) {
  return useQuery({
    queryKey: ["audits", id],
    queryFn: ({ signal }) => apiGet<AuditDetail>(`/audits/${id}`, signal),
    enabled: id !== null,
    refetchInterval: (query) => {
      const data = query.state.data as AuditDetail | undefined;
      // Poll while still running (output empty + no error means in-flight)
      if (data && !data.output && !data.error) return 5_000;
      return false;
    },
  });
}

export function useTriggerAudit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (propertyId: number) =>
      apiPost<{ audit_id: number; property_id: number; status: string; message: string }>(
        `/properties/${propertyId}/audit`, {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["audits"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

interface FindingsArgs {
  status?: FindingStatus;
  severity?: Severity;
  property_id?: number;
  audit_id?: number;
  limit?: number;
  offset?: number;
}

export function useFindings(args: FindingsArgs = {}) {
  const qs = new URLSearchParams();
  if (args.status)   qs.set("status",   args.status);
  if (args.severity) qs.set("severity", args.severity);
  if (args.property_id !== undefined) qs.set("property_id", String(args.property_id));
  if (args.audit_id !== undefined)    qs.set("audit_id",    String(args.audit_id));
  qs.set("limit",  String(args.limit  ?? 100));
  qs.set("offset", String(args.offset ?? 0));
  return useQuery({
    queryKey: ["findings", args],
    queryFn: ({ signal }) => apiGet<FindingList>(`/findings?${qs.toString()}`, signal),
    refetchInterval: 60_000,
  });
}

export function useUpdateFindingStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: number; status: FindingStatus }) => {
      const r = await fetch(`/api/findings/${id}`, {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (!r.ok) throw new Error(`PATCH /findings/${id} failed: ${r.status}`);
      return r.json() as Promise<Finding>;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["findings"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

// ---- Dashboard summary (one-shot for the Dashboard page) ----------------

export interface DashboardKpisExtended {
  properties_active: number;
  findings_open: number;
  findings_critical: number;
  findings_high: number;
  audits_7d: number;
  reports_30d: number;
}

export interface DashboardSummary {
  kpis: DashboardKpisExtended;
  recent_audits: Audit[];
  top_open_findings: Finding[];
}

export function useDashboardSummary() {
  return useQuery({
    queryKey: ["dashboard", "summary"],
    queryFn: ({ signal }) => apiGet<DashboardSummary>("/dashboard/summary", signal),
    refetchInterval: 30_000,
  });
}

// ---- Chat (in-app conversation surface) -----------------------------------

export interface ChatConversationMeta {
  id: number;
  title: string;
  // When set, this conversation is scoped to one property — every chat
  // message gets that property's full dossier injected into the prompt
  // (substrate + headline metrics + cross-source insights + open findings).
  // Null = unscoped (general chat). Set when the chat panel is opened
  // from a property page (chat panel passes property_id at create time).
  property_id: number | null;
  created_at: string;
  last_message_at: string;
  archived: boolean;
}

export interface ChatMessage {
  id: number;
  role: "user" | "assistant";
  content: string;
  created_at: string;
  audit_id: number | null;
  duration_s: number | null;
  ok: boolean | null;
  error: string | null;
  meta?: Record<string, unknown>;
}

export interface ChatConversation extends ChatConversationMeta {
  messages: ChatMessage[];
}

export interface ChatConversationsList {
  items: ChatConversationMeta[];
  total: number;
}

export function useChatConversations(enabled = true) {
  return useQuery({
    queryKey: ["chat", "conversations"],
    queryFn: ({ signal }) =>
      apiGet<ChatConversationsList>("/chat/conversations", signal),
    enabled,
    staleTime: 30_000,
  });
}

export function useChatConversation(id: number | null) {
  return useQuery({
    queryKey: ["chat", "conversation", id],
    queryFn: ({ signal }) =>
      apiGet<ChatConversation>(`/chat/conversations/${id}`, signal),
    enabled: id !== null,
  });
}

export function useCreateChatConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { title?: string | null; property_id?: number | null }) =>
      apiPost<
        ChatConversationMeta,
        { title?: string | null; property_id?: number | null }
      >("/chat/conversations", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chat", "conversations"] });
    },
  });
}

export type ChatStreamEvent =
  | { type: "user_message"; message: ChatMessage }
  | { type: "thinking"; elapsed_s: number }
  | { type: "assistant_message"; message: ChatMessage }
  | { type: "error"; error: string };

export async function streamChatMessage(
  conversationId: number,
  content: string,
  onEvent: (e: ChatStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const r = await fetch(`/api/chat/conversations/${conversationId}/messages`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", Accept: "application/x-ndjson" },
    body: JSON.stringify({ content }),
    signal,
  });
  if (!r.ok || !r.body) {
    const text = await r.text().catch(() => "");
    throw new Error(`POST chat failed: ${r.status} ${text}`);
  }
  const reader = r.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const lines = buf.split("\n");
    buf = lines.pop() ?? "";
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      try {
        onEvent(JSON.parse(trimmed) as ChatStreamEvent);
      } catch {
        // Tolerate any junk line — keep streaming.
      }
    }
  }
  if (buf.trim()) {
    try { onEvent(JSON.parse(buf) as ChatStreamEvent); } catch { /* ignore */ }
  }
}

export function useSendChatMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      conversationId, content, onEvent, signal,
    }: {
      conversationId: number;
      content: string;
      onEvent: (e: ChatStreamEvent) => void;
      signal?: AbortSignal;
    }) => {
      await streamChatMessage(conversationId, content, onEvent, signal);
    },
    onSettled: (_data, _err, { conversationId }) => {
      qc.invalidateQueries({ queryKey: ["chat", "conversation", conversationId] });
      qc.invalidateQueries({ queryKey: ["chat", "conversations"] });
    },
  });
}

export function useArchiveChatConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const r = await fetch(`/api/chat/conversations/${id}`, {
        method: "DELETE",
        credentials: "same-origin",
      });
      if (!r.ok) throw new Error(`DELETE failed: ${r.status}`);
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chat", "conversations"] });
    },
  });
}

// ─── Google OAuth (per-property) ──────────────────────────────────

export interface GoogleConnectionStatus {
  connected: boolean;
  account_email: string | null;
  scopes: string | null;
  connected_at: string | null;
  has_oauth_client: boolean;
}

export function useGoogleOAuthStatus(propertyId: number | null) {
  return useQuery({
    queryKey: ["oauth", "google", "status", propertyId],
    queryFn: ({ signal }) =>
      apiGet<GoogleConnectionStatus>(`/oauth/google/status?property_id=${propertyId}`, signal),
    enabled: propertyId !== null && propertyId > 0,
    staleTime: 30_000,
  });
}

export function useStartGoogleOAuth() {
  return useMutation({
    mutationFn: async (body: { property_id: number; return_to?: string; login_hint?: string }) => {
      const r = await fetch("/api/oauth/google/start", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          property_id: body.property_id,
          return_to: body.return_to ?? window.location.pathname + window.location.search,
          login_hint: body.login_hint,
        }),
      });
      if (!r.ok) throw new Error(`POST /oauth/google/start failed: ${r.status}`);
      return r.json() as Promise<{ auth_url: string; state: string }>;
    },
  });
}

export function useDisconnectGoogleOAuth() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (propertyId: number) => {
      const r = await fetch(`/api/oauth/google?property_id=${propertyId}`, {
        method: "DELETE",
        credentials: "same-origin",
      });
      if (!r.ok) throw new Error(`DELETE /oauth/google failed: ${r.status}`);
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["oauth", "google"] });
    },
  });
}

// ─── Capabilities (vendor inventory + status) ────────────────────

export interface CapabilityEndpoint {
  vendor: string;
  endpoint_name: string;
  label: string;
  category: string;
  description: string;
  value_props: string[];
  cost: string;
  docs_url: string;
  auriel_status: "not_wired" | "wired" | "wired_substrate" | "quota_blocked";
  current_pull: string | null;
  recommended_pull: string | null;
  unlocks: string[];
  calls_30d: number;
  errors_30d: number;
  last_called_at: string | null;
  latest_row_id: number | null;
}

export interface VendorBlock {
  vendor: string;
  display: string;
  kind: "free" | "paid" | "internal";
  auth: string;
  endpoints: CapabilityEndpoint[];
  total_endpoints: number;
  wired_count: number;
  substrate_count: number;
  not_wired_count: number;
}

export interface CapabilitiesOut {
  vendors: VendorBlock[];
  catalog_total: number;
}

export interface CapabilitySample {
  available: boolean;
  row_id: number | null;
  fetched_at: string | null;
  target: string | null;
  params: Record<string, unknown> | null;
  response: unknown;
  response_size_bytes: number | null;
  error: string | null;
}

export function useCapabilities(daysBack: number = 30) {
  return useQuery({
    queryKey: ["capabilities", daysBack],
    queryFn: ({ signal }) =>
      apiGet<CapabilitiesOut>(`/capabilities?days_back=${daysBack}`, signal),
    staleTime: 60_000,
  });
}

export function useCapabilitySample(
  vendor: string | null,
  endpoint: string | null,
  opts?: { propertyId?: number },
) {
  return useQuery({
    queryKey: ["capabilities", "sample", vendor, endpoint, opts?.propertyId],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ endpoint: endpoint! });
      if (opts?.propertyId) qs.set("property_id", String(opts.propertyId));
      return apiGet<CapabilitySample>(`/capabilities/${vendor}/sample?${qs.toString()}`, signal);
    },
    enabled: vendor !== null && endpoint !== null,
  });
}

// ─── Ahrefs bulk pull (per-property "Refresh" button) ────────────

export interface AhrefsPullResult {
  endpoint: string;
  ok: boolean;
  rows: number | null;
  error: string | null;
}

export interface AhrefsPullResponse {
  property_id: number;
  domain: string;
  forced: boolean;
  duration_s: number;
  results: AhrefsPullResult[];
}

export function useStartAhrefsPull(propertyId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { force?: boolean }) => {
      const r = await fetch(`/api/properties/${propertyId}/ahrefs/pull`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ force: body.force ?? false }),
      });
      if (!r.ok) {
        let detail = `${r.status}`;
        try { detail = (await r.json()).detail || detail; } catch { /* ignore */ }
        throw new Error(detail);
      }
      return r.json() as Promise<AhrefsPullResponse>;
    },
    onSuccess: () => {
      // Invalidate every capabilities sample query — they all might
      // have new data now
      qc.invalidateQueries({ queryKey: ["capabilities", "sample"] });
      qc.invalidateQueries({ queryKey: ["capabilities"] });
      qc.invalidateQueries({ queryKey: ["inspector"] });
    },
  });
}

// ─── Ahrefs Keywords Explorer research (on-demand) ────────────────

export interface KeywordResearchOverview {
  keyword?: string;
  volume?: number | null;
  difficulty?: number | null;
  cpc?: number | null;
  clicks?: number | null;
  return_rate?: number | null;
  is_commercial?: boolean | null;
}

export interface KeywordResearchTerm {
  keyword: string;
  volume?: number | null;
  difficulty?: number | null;
  cpc?: number | null;
  clicks?: number | null;
}

export interface KeywordResearchResponse {
  keyword: string;
  country: string;
  duration_s: number;
  overview: KeywordResearchOverview | null;
  matching_terms: KeywordResearchTerm[] | null;
  related_terms: KeywordResearchTerm[] | null;
  volume_history: Array<{ date?: string; volume?: number }> | null;
  errors: Record<string, string>;
}

export function useKeywordResearch(propertyId: number) {
  return useMutation({
    mutationFn: async (body: { keyword: string; country?: string }) => {
      const r = await fetch(`/api/properties/${propertyId}/ahrefs/keyword-research`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ keyword: body.keyword, country: body.country ?? "ro" }),
      });
      if (!r.ok) {
        let detail = `${r.status}`;
        try { detail = (await r.json()).detail || detail; } catch { /* ignore */ }
        throw new Error(detail);
      }
      return r.json() as Promise<KeywordResearchResponse>;
    },
  });
}


// ─── CWV bulk pull ────────────────────────────────────────────────

export interface CWVPullResult {
  endpoint: string;
  ok: boolean;
  rows: number | null;
  error: string | null;
}

export interface CWVPullResponse {
  property_id: number;
  domain: string;
  forced: boolean;
  duration_s: number;
  results: CWVPullResult[];
  projection: { field_upserts: number; lab_upserts: number } | null;
}

export function useStartCWVPull(propertyId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { force?: boolean }) => {
      const r = await fetch(`/api/properties/${propertyId}/cwv/pull`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ force: body.force ?? false }),
      });
      if (!r.ok) {
        let detail = `${r.status}`;
        try { detail = (await r.json()).detail || detail; } catch { /* ignore */ }
        throw new Error(detail);
      }
      return r.json() as Promise<CWVPullResponse>;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["capabilities", "sample"] });
      qc.invalidateQueries({ queryKey: ["capabilities"] });
    },
  });
}


// ─── GSC batch URL inspection ────────────────────────────────────

export interface GSCBatchInspectResult {
  url: string;
  ok: boolean;
  indexed: boolean | null;
  coverage_state: string | null;
  error: string | null;
}

export interface GSCBatchInspectResponse {
  property_id: number;
  duration_s: number;
  requested: number;
  inspected: number;
  skipped: number;
  errors: number;
  results: GSCBatchInspectResult[];
}

export function useGSCBatchInspect(propertyId: number) {
  return useMutation({
    mutationFn: async (body: { limit?: number; force?: boolean }) => {
      const r = await fetch(`/api/properties/${propertyId}/gsc/batch-inspect`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          limit: body.limit ?? 20,
          force: body.force ?? false,
        }),
      });
      if (!r.ok) {
        let detail = `${r.status}`;
        try { detail = (await r.json()).detail || detail; } catch { /* ignore */ }
        throw new Error(detail);
      }
      return r.json() as Promise<GSCBatchInspectResponse>;
    },
  });
}


// ─── DataForSEO bulk pull ────────────────────────────────────────

export interface DFSPullResult {
  endpoint: string;
  ok: boolean;
  rows: number | null;
  error: string | null;
}

export interface DFSPullResponse {
  property_id: number;
  domain: string;
  forced: boolean;
  duration_s: number;
  results: DFSPullResult[];
}

export function useStartDataForSEOPull(propertyId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { force?: boolean }) => {
      const r = await fetch(`/api/properties/${propertyId}/dataforseo/pull`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ force: body.force ?? false }),
      });
      if (!r.ok) {
        let detail = `${r.status}`;
        try { detail = (await r.json()).detail || detail; } catch { /* ignore */ }
        throw new Error(detail);
      }
      return r.json() as Promise<DFSPullResponse>;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["capabilities", "sample"] });
      qc.invalidateQueries({ queryKey: ["capabilities"] });
      qc.invalidateQueries({ queryKey: ["inspector"] });
    },
  });
}

// Focused refresh — fires JUST google_reviews + trustpilot_reviews
// (~30-90s). Used by the Reviews page Refresh button so the user
// doesn't have to trigger the full 25-endpoint bulk pull just to
// see fresh review data.
export function useRefreshReviews(propertyId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { force?: boolean } = {}) => {
      const r = await fetch(`/api/properties/${propertyId}/reviews/refresh`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ force: body.force ?? true }),  // default force=true: user clicked Refresh
      });
      if (!r.ok) {
        let detail = `${r.status}`;
        try { detail = (await r.json()).detail || detail; } catch { /* ignore */ }
        throw new Error(detail);
      }
      return r.json() as Promise<DFSPullResponse>;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["capabilities", "sample"] });
    },
  });
}

// ─── GSC bulk pull ────────────────────────────────────────────────

export interface VendorPullResult {
  endpoint: string;
  ok: boolean;
  rows: number | null;
  error: string | null;
}

export interface VendorPullResponse {
  property_id: number;
  domain: string;
  forced: boolean;
  duration_s: number;
  results: VendorPullResult[];
}

function makeVendorPullHook(slug: string) {
  return function useStartVendorPull(propertyId: number) {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: async (body: { force?: boolean }) => {
        const r = await fetch(`/api/properties/${propertyId}/${slug}/pull`, {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ force: body.force ?? false }),
        });
        if (!r.ok) {
          let detail = `${r.status}`;
          try { detail = (await r.json()).detail || detail; } catch { /* ignore */ }
          throw new Error(detail);
        }
        return r.json() as Promise<VendorPullResponse>;
      },
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ["capabilities", "sample"] });
        qc.invalidateQueries({ queryKey: ["capabilities"] });
        qc.invalidateQueries({ queryKey: ["inspector"] });
      },
    });
  };
}

export const useStartGSCPull = makeVendorPullHook("gsc");
export const useStartGA4Pull = makeVendorPullHook("ga4");
export const useStartServerLogsPull = makeVendorPullHook("server-logs");

// ─── Insights (cross-source extractors) ──────────────────────────

export interface InsightFinding {
  id: number;
  kind: string;
  severity: "critical" | "high" | "medium" | "low" | "info";
  title: string;
  detail: string;
  detected_at: string;
  metadata: Record<string, unknown>;
  audit_id: number | null;
}

export interface FindingsList {
  items: InsightFinding[];
  total: number;
}

export interface ExtractInsightsResponse {
  property_id: number;
  domain: string;
  found: number;
  by_severity: Record<string, number>;
  finding_ids: number[];
}

export function useExtractInsights(propertyId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/properties/${propertyId}/insights/extract`, {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
      });
      if (!r.ok) {
        let detail = `${r.status}`;
        try { detail = (await r.json()).detail || detail; } catch { /* ignore */ }
        throw new Error(detail);
      }
      return r.json() as Promise<ExtractInsightsResponse>;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["insights", "findings", propertyId] });
    },
  });
}

export function useInsightsFindings(propertyId: number | null) {
  return useQuery({
    queryKey: ["insights", "findings", propertyId],
    queryFn: ({ signal }) =>
      apiGet<FindingsList>(`/properties/${propertyId}/insights/findings`, signal),
    enabled: propertyId !== null && propertyId > 0,
  });
}

// ─── Cross-source Pages lens ─────────────────────────────────────

export interface PageRow {
  url: string;
  status_code: number | null;
  title: string | null;
  title_length: number | null;
  meta_description: string | null;
  h1: string | null;
  word_count: number | null;
  inlinks: number | null;
  outlinks: number | null;
  crawl_depth: number | null;
  indexability: string | null;
  indexability_status: string | null;
  canonical_url: string | null;
  gsc_clicks: number | null;
  gsc_impressions: number | null;
  gsc_ctr: number | null;
  gsc_position: number | null;
  ga4_sessions: number | null;
  ga4_bounce_rate: number | null;
  ga4_engagement_rate: number | null;
  dfs_keywords: number | null;
  dfs_etv: number | null;
  dfs_backlinks: number | null;
  dfs_referring_domains: number | null;
}

export interface PagesLensFilters {
  search?: string;
  status_class?: "2xx" | "3xx" | "4xx" | "5xx";
  missing_title?: boolean;
  missing_meta?: boolean;
  indexability?: string;
  has_gsc_clicks?: boolean;
  sort?: string;
  sort_dir?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

export interface PagesLensOut {
  items: PageRow[];
  total: number;
  crawl_id: number | null;
  sources_present: { crawl: boolean; gsc: boolean; ga4: boolean; dfs: boolean };
}

export function usePropertyPages(propertyId: number | null, filters: PagesLensFilters) {
  const qs = new URLSearchParams();
  if (filters.search) qs.set("search", filters.search);
  if (filters.status_class) qs.set("status_class", filters.status_class);
  if (filters.missing_title !== undefined) qs.set("missing_title", String(filters.missing_title));
  if (filters.missing_meta !== undefined) qs.set("missing_meta", String(filters.missing_meta));
  if (filters.indexability) qs.set("indexability", filters.indexability);
  if (filters.has_gsc_clicks !== undefined) qs.set("has_gsc_clicks", String(filters.has_gsc_clicks));
  if (filters.sort) qs.set("sort", filters.sort);
  if (filters.sort_dir) qs.set("sort_dir", filters.sort_dir);
  qs.set("limit", String(filters.limit ?? 100));
  qs.set("offset", String(filters.offset ?? 0));
  return useQuery({
    queryKey: ["pages-lens", propertyId, qs.toString()],
    queryFn: ({ signal }) =>
      apiGet<PagesLensOut>(`/properties/${propertyId}/pages?${qs.toString()}`, signal),
    enabled: propertyId !== null && propertyId > 0,
    staleTime: 30_000,
  });
}

// ─── Cross-source Keywords lens ──────────────────────────────────

export interface KeywordRow {
  keyword: string;
  gsc_clicks: number | null;
  gsc_impressions: number | null;
  gsc_ctr: number | null;
  gsc_position: number | null;
  ah_position: number | null;
  ah_url: string | null;
  ah_volume: number | null;
  ah_traffic: number | null;
  ah_difficulty: number | null;
  dfs_position: number | null;
  dfs_url: string | null;
  dfs_volume: number | null;
  dfs_cpc: number | null;
  in_gsc: boolean;
  in_ahrefs: boolean;
  in_dfs: boolean;
}

export interface KeywordsLensFilters {
  search?: string;
  only_in_gsc?: boolean;
  only_in_ahrefs?: boolean;
  only_in_dfs?: boolean;
  only_disagreement?: boolean;
  sort?: string;
  sort_dir?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

export interface KeywordsLensOut {
  items: KeywordRow[];
  total: number;
  sources_present: { gsc: boolean; ahrefs: boolean; dfs: boolean };
}

export function usePropertyKeywords(propertyId: number | null, filters: KeywordsLensFilters) {
  const qs = new URLSearchParams();
  if (filters.search) qs.set("search", filters.search);
  if (filters.only_in_gsc) qs.set("only_in_gsc", "true");
  if (filters.only_in_ahrefs) qs.set("only_in_ahrefs", "true");
  if (filters.only_in_dfs) qs.set("only_in_dfs", "true");
  if (filters.only_disagreement) qs.set("only_disagreement", "true");
  if (filters.sort) qs.set("sort", filters.sort);
  if (filters.sort_dir) qs.set("sort_dir", filters.sort_dir);
  qs.set("limit", String(filters.limit ?? 200));
  qs.set("offset", String(filters.offset ?? 0));
  return useQuery({
    queryKey: ["keywords-lens", propertyId, qs.toString()],
    queryFn: ({ signal }) =>
      apiGet<KeywordsLensOut>(`/properties/${propertyId}/keywords?${qs.toString()}`, signal),
    enabled: propertyId !== null && propertyId > 0,
    staleTime: 30_000,
  });
}

// ─── Cross-source Backlinks lens ──────────────────────────────────

export interface BacklinkDomainRow {
  domain: string;
  ah_dr: number | null;
  ah_links: number | null;
  ah_dofollow_linked_domains: number | null;
  ah_first_seen: string | null;
  dfs_rank: number | null;
  dfs_backlinks: number | null;
  dfs_first_seen: string | null;
  dfs_lost_date: string | null;
  in_ahrefs: boolean;
  in_dfs: boolean;
  // Lifecycle (from refdomains substrate; first projected 2026-05-11)
  first_seen_at: string | null;
  last_seen_at: string | null;
  is_lost: boolean;
  lost_at: string | null;
}

export interface BacklinksLensFilters {
  search?: string;
  only_in_ahrefs?: boolean;
  only_in_dfs?: boolean;
  only_disagreement?: boolean;
  only_lost?: boolean;
  only_active?: boolean;  // default true on backend; pass false to include lost
  min_dr?: number;
  sort?: string;
  sort_dir?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

export interface BacklinksLensOut {
  items: BacklinkDomainRow[];
  total: number;
  sources_present: { ahrefs: boolean; dfs: boolean };
  headline_counts: {
    ahrefs_refdomains: number;
    dfs_refdomains: number;
    overlap: number;
    ahrefs_only: number;
    dfs_only: number;
    broken_backlinks: number;
    // Lifecycle aggregates (substrate)
    active_total?: number;
    lost_total?: number;
    lost_last_7d?: number;
    lost_last_30d?: number;
    gained_last_7d?: number;
    gained_last_30d?: number;
  };
}

// ─── Per-link substrate view (migration 0010) ────────────────────

export interface BacklinkLinkRow {
  url_from: string;
  url_to: string;
  anchor: string;
  refdomain: string | null;
  is_dofollow: boolean;
  source_dr: number | null;
  source_traffic: number | null;
  ah_first_seen: string | null;
  ah_last_seen: string | null;
  in_ahrefs_live: boolean;
  first_seen_at: string | null;
  last_seen_at: string | null;
  is_lost: boolean;
  lost_at: string | null;
}

export interface BacklinkLinksFilters {
  search?: string;
  only_dofollow?: boolean;
  only_lost?: boolean;
  only_active?: boolean;
  refdomain?: string;
  min_dr?: number;
  sort?: string;
  sort_dir?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

export interface BacklinkLinksOut {
  items: BacklinkLinkRow[];
  total: number;
  headline_counts: {
    active_total: number;
    active_dofollow: number;
    lost_total: number;
    lost_last_7d: number;
    lost_last_30d: number;
    gained_last_7d: number;
    gained_last_30d: number;
  };
}

export function usePropertyBacklinkLinks(propertyId: number | null, filters: BacklinkLinksFilters) {
  const qs = new URLSearchParams();
  if (filters.search) qs.set("search", filters.search);
  if (filters.only_dofollow) qs.set("only_dofollow", "true");
  if (filters.only_lost) qs.set("only_lost", "true");
  if (filters.only_active === false) qs.set("only_active", "false");
  if (filters.refdomain) qs.set("refdomain", filters.refdomain);
  if (filters.min_dr !== undefined) qs.set("min_dr", String(filters.min_dr));
  if (filters.sort) qs.set("sort", filters.sort);
  if (filters.sort_dir) qs.set("sort_dir", filters.sort_dir);
  qs.set("limit", String(filters.limit ?? 200));
  qs.set("offset", String(filters.offset ?? 0));
  return useQuery({
    queryKey: ["backlinks-links", propertyId, qs.toString()],
    queryFn: ({ signal }) =>
      apiGet<BacklinkLinksOut>(`/properties/${propertyId}/backlinks/links?${qs.toString()}`, signal),
    enabled: propertyId !== null && propertyId > 0,
    staleTime: 30_000,
  });
}


export function usePropertyBacklinks(propertyId: number | null, filters: BacklinksLensFilters) {
  const qs = new URLSearchParams();
  if (filters.search) qs.set("search", filters.search);
  if (filters.only_in_ahrefs) qs.set("only_in_ahrefs", "true");
  if (filters.only_in_dfs) qs.set("only_in_dfs", "true");
  if (filters.only_disagreement) qs.set("only_disagreement", "true");
  if (filters.only_lost) qs.set("only_lost", "true");
  if (filters.only_active === false) qs.set("only_active", "false");
  if (filters.min_dr !== undefined) qs.set("min_dr", String(filters.min_dr));
  if (filters.sort) qs.set("sort", filters.sort);
  if (filters.sort_dir) qs.set("sort_dir", filters.sort_dir);
  qs.set("limit", String(filters.limit ?? 200));
  qs.set("offset", String(filters.offset ?? 0));
  return useQuery({
    queryKey: ["backlinks-lens", propertyId, qs.toString()],
    queryFn: ({ signal }) =>
      apiGet<BacklinksLensOut>(`/properties/${propertyId}/backlinks?${qs.toString()}`, signal),
    enabled: propertyId !== null && propertyId > 0,
    staleTime: 30_000,
  });
}

// ─── Inspector (source_data debug surface) ───────────────────────

export interface SourceDataRow {
  id: number;
  tenant_id: string;
  property_id: number | null;
  audit_id: number | null;
  source: string;
  endpoint: string;
  target: string;
  fetched_at: string;
  cost_units: number | null;
  has_error: boolean;
  error: string | null;
  response_size_bytes: number;
}

export interface SourceDataDetail extends SourceDataRow {
  params: Record<string, unknown>;
  response: unknown;
}

export interface SourceDataListOut {
  items: SourceDataRow[];
  total: number;
}

export interface SourceDataFilters {
  source?: string;
  property_id?: number;
  endpoint_like?: string;
  target_like?: string;
  has_error?: boolean;
  days_back?: number;
  limit?: number;
  offset?: number;
}

export interface SourcesSummaryRow {
  source: string;
  total: number;
  errors: number;
  last_fetched_at: string | null;
  properties_covered: number;
}

export interface SourcesSummaryOut {
  sources: SourcesSummaryRow[];
  window_days: number;
}

export function useInspectorSourceData(filters: SourceDataFilters) {
  const qs = new URLSearchParams();
  if (filters.source) qs.set("source", filters.source);
  if (filters.property_id) qs.set("property_id", String(filters.property_id));
  if (filters.endpoint_like) qs.set("endpoint_like", filters.endpoint_like);
  if (filters.target_like) qs.set("target_like", filters.target_like);
  if (filters.has_error !== undefined) qs.set("has_error", String(filters.has_error));
  qs.set("days_back", String(filters.days_back ?? 7));
  qs.set("limit", String(filters.limit ?? 100));
  qs.set("offset", String(filters.offset ?? 0));
  return useQuery({
    queryKey: ["inspector", "source-data", qs.toString()],
    queryFn: ({ signal }) =>
      apiGet<SourceDataListOut>(`/inspector/source-data?${qs.toString()}`, signal),
    staleTime: 15_000,
  });
}

export function useInspectorRow(rowId: number | null) {
  return useQuery({
    queryKey: ["inspector", "row", rowId],
    queryFn: ({ signal }) =>
      apiGet<SourceDataDetail>(`/inspector/source-data/${rowId}`, signal),
    enabled: rowId !== null && rowId > 0,
  });
}

export function useInspectorSourcesSummary(daysBack: number = 7) {
  return useQuery({
    queryKey: ["inspector", "sources-summary", daysBack],
    queryFn: ({ signal }) =>
      apiGet<SourcesSummaryOut>(`/inspector/sources-summary?days_back=${daysBack}`, signal),
    staleTime: 30_000,
  });
}

// ─── Crawls + Pages lens (substrate-first Frog) ───────────────────

export interface Crawl {
  id: number;
  tenant_id: string;
  property_id: number;
  status: "queued" | "running" | "completed" | "timed_out" | "failed" | "cancelled" | "paused";
  triggered_by: string;
  triggered_by_user_id: number | null;
  start_url: string;
  max_urls: number;
  started_at: string | null;
  finished_at: string | null;
  duration_s: number | null;
  pages_found: number | null;
  broken_count: number | null;
  missing_title_count: number | null;
  missing_meta_count: number | null;
  duplicate_title_count: number | null;
  status_code_counts: Record<string, number> | null;
  frog_exit_code: number | null;
  frog_run_dir: string | null;
  error: string | null;
  created_at: string;
}

export interface CrawlList {
  items: Crawl[];
  total: number;
}

export interface CrawlPage {
  id: number;
  url: string;
  status_code: number | null;
  content_type: string | null;
  title: string | null;
  title_length: number | null;
  meta_description: string | null;
  meta_description_length: number | null;
  h1: string | null;
  word_count: number | null;
  size_bytes: number | null;
  response_time_ms: number | null;
  crawl_depth: number | null;
  inlinks: number | null;
  outlinks: number | null;
  canonical_url: string | null;
  meta_robots: string | null;
  indexability: string | null;
  indexability_status: string | null;
  redirect_url: string | null;
}

export interface CrawlPageList {
  items: CrawlPage[];
  total: number;
  crawl_id: number;
}

export interface CrawlPageFilters {
  search?: string;
  status_class?: "2xx" | "3xx" | "4xx" | "5xx";
  missing_title?: boolean;
  missing_meta?: boolean;
  indexability?: "Indexable" | "Non-Indexable";
  sort?: string;
  sort_dir?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

export function useCrawls(propertyId: number | null) {
  return useQuery({
    queryKey: ["crawls", "list", propertyId],
    queryFn: ({ signal }) =>
      apiGet<CrawlList>(`/properties/${propertyId}/crawls`, signal),
    enabled: propertyId !== null && propertyId > 0,
    // Refetch every 5s while there's a running crawl — see Crawl page
    // for the smarter version that only polls when relevant.
    staleTime: 5_000,
  });
}

export function useCrawl(crawlId: number | null, opts?: { pollMs?: number }) {
  return useQuery({
    queryKey: ["crawls", "detail", crawlId],
    queryFn: ({ signal }) =>
      apiGet<Crawl>(`/crawls/${crawlId}`, signal),
    enabled: crawlId !== null && crawlId > 0,
    refetchInterval: (query) => {
      // Live-poll while the crawl is still in flight.
      const data = query.state.data as Crawl | undefined;
      if (data && (data.status === "queued" || data.status === "running")) {
        return opts?.pollMs ?? 5_000;
      }
      return false;
    },
  });
}

// ─── Live SpiderProgress streaming ────────────────────────────────
//
// Backend SSH-tails forge's frog.stdout.log and emits parsed
// SpiderProgress events as NDJSON. The hook below opens that
// stream, accumulates the latest progress state, and returns it
// for the UI to render (live counters + bar + ETA).
//
// One stream per crawl_id. Auto-closes on terminal events. Safe to
// open even on completed crawls — backend sends a single snapshot
// + done.

export interface CrawlProgressState {
  // Connection lifecycle
  connected: boolean;     // first event received
  done: boolean;          // backend sent {type:"done"}
  doneStatus: string | null;
  error: string | null;
  lastUpdateAt: number | null;  // Date.now() of latest progress event

  // Latest SpiderProgress snapshot
  active: number | null;          // worker threads currently busy
  completed: number | null;
  waiting: number | null;
  pct: number | null;             // Frog's own % (denominator moves)

  // Derived (server-computed over a 30-sample window)
  rate_url_per_s: number | null;
  discovery_per_s: number | null;
  eta_seconds: number | null;

  // Phase 3 (2026-05-13) — filter-explosion detector
  explosion_warning: boolean;
  telemetry: {
    samples?: number;
    total_discovered?: number;
    queue_growth_ratio?: number | null;
    discovery_to_completion_ratio?: number | null;
  };
}

const INITIAL_PROGRESS: CrawlProgressState = {
  connected: false, done: false, doneStatus: null, error: null,
  lastUpdateAt: null,
  active: null, completed: null, waiting: null, pct: null,
  rate_url_per_s: null, discovery_per_s: null, eta_seconds: null,
  explosion_warning: false, telemetry: {},
};

interface CrawlProgressEvent {
  type: "connected" | "progress" | "heartbeat" | "done" | "error";
  status?: string;
  active?: number;
  completed?: number;
  waiting?: number;
  pct?: number;
  rate_url_per_s?: number | null;
  discovery_per_s?: number | null;
  eta_seconds?: number | null;
  explosion_warning?: boolean;
  telemetry?: CrawlProgressState["telemetry"];
  error?: string;
  reason?: string;
}

export function useCrawlProgressStream(
  crawlId: number | null,
  enabled: boolean = true,
): CrawlProgressState {
  const [state, setState] = useState<CrawlProgressState>(INITIAL_PROGRESS);

  useEffect(() => {
    if (!crawlId || !enabled) {
      setState(INITIAL_PROGRESS);
      return;
    }
    const ctrl = new AbortController();
    let cancelled = false;
    setState(INITIAL_PROGRESS);

    (async () => {
      try {
        const r = await fetch(`/api/crawls/${crawlId}/progress/stream`, {
          credentials: "same-origin",
          signal: ctrl.signal,
        });
        if (!r.ok || !r.body) {
          setState(s => ({ ...s, error: `HTTP ${r.status}` }));
          return;
        }
        const reader = r.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        while (!cancelled) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let nl: number;
          while ((nl = buffer.indexOf("\n")) >= 0) {
            const line = buffer.slice(0, nl).trim();
            buffer = buffer.slice(nl + 1);
            if (!line) continue;
            try {
              const ev: CrawlProgressEvent = JSON.parse(line);
              if (ev.type === "connected") {
                setState(s => ({ ...s, connected: true }));
              } else if (ev.type === "progress") {
                setState(s => ({
                  ...s,
                  connected: true,
                  lastUpdateAt: Date.now(),
                  active: ev.active ?? s.active,
                  completed: ev.completed ?? s.completed,
                  waiting: ev.waiting ?? s.waiting,
                  pct: ev.pct ?? s.pct,
                  rate_url_per_s: ev.rate_url_per_s ?? null,
                  discovery_per_s: ev.discovery_per_s ?? null,
                  eta_seconds: ev.eta_seconds ?? null,
                  explosion_warning: ev.explosion_warning ?? false,
                  telemetry: ev.telemetry ?? s.telemetry,
                }));
              } else if (ev.type === "done") {
                setState(s => ({
                  ...s, done: true, doneStatus: ev.status ?? null,
                }));
              } else if (ev.type === "error") {
                setState(s => ({ ...s, error: ev.error ?? "unknown" }));
              }
              // heartbeat: nothing to update, just keeps stream alive
            } catch {
              // skip malformed line
            }
          }
        }
      } catch (e: unknown) {
        if ((e as Error)?.name !== "AbortError") {
          setState(s => ({ ...s, error: String((e as Error)?.message || e) }));
        }
      }
    })();

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [crawlId, enabled]);

  return state;
}

/** Format seconds → "1h 23m" / "32m" / "<1m". */
export function formatEtaSeconds(s: number | null | undefined): string {
  if (s == null || !Number.isFinite(s) || s <= 0) return "—";
  if (s < 60) return "<1m";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}h ${mm}m`;
}

export function useCrawlPages(crawlId: number | null, filters: CrawlPageFilters) {
  const qs = new URLSearchParams();
  if (filters.search) qs.set("search", filters.search);
  if (filters.status_class) qs.set("status_class", filters.status_class);
  if (filters.missing_title !== undefined) qs.set("missing_title", String(filters.missing_title));
  if (filters.missing_meta !== undefined) qs.set("missing_meta", String(filters.missing_meta));
  if (filters.indexability) qs.set("indexability", filters.indexability);
  if (filters.sort) qs.set("sort", filters.sort);
  if (filters.sort_dir) qs.set("sort_dir", filters.sort_dir);
  qs.set("limit", String(filters.limit ?? 100));
  qs.set("offset", String(filters.offset ?? 0));
  const url = `/crawls/${crawlId}/pages?${qs.toString()}`;
  return useQuery({
    queryKey: ["crawls", "pages", crawlId, qs.toString()],
    queryFn: ({ signal }) => apiGet<CrawlPageList>(url, signal),
    enabled: crawlId !== null && crawlId > 0,
    staleTime: 30_000,
  });
}

export interface StartCrawlBody {
  max_urls?: number;
  // Throttle (per-crawl). When omitted, backend falls back to the
  // property's defaults. Send explicitly from the speed-preset dropdown.
  max_threads?: number;
  max_url_per_sec?: number;
}

export function useStartCrawl(propertyId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: StartCrawlBody) => {
      const payload: Record<string, number> = {
        max_urls: body.max_urls ?? 1_000,
      };
      if (body.max_threads != null) payload.max_threads = body.max_threads;
      if (body.max_url_per_sec != null) payload.max_url_per_sec = body.max_url_per_sec;
      const r = await fetch(`/api/properties/${propertyId}/crawls/start`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        let detail = `${r.status}`;
        try { detail = (await r.json()).detail || detail; } catch { /* ignore */ }
        throw new Error(detail);
      }
      return r.json() as Promise<{ crawl_id: number; status: string }>;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crawls", "list", propertyId] });
    },
  });
}

export function useCancelCrawl(crawlId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/crawls/${crawlId}/cancel`, {
        method: "POST",
        credentials: "same-origin",
      });
      if (!r.ok) throw new Error(`POST /crawls/${crawlId}/cancel failed: ${r.status}`);
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crawls"] });
    },
  });
}

export function usePauseCrawl(crawlId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/crawls/${crawlId}/pause`, {
        method: "POST",
        credentials: "same-origin",
      });
      if (!r.ok) throw new Error(`POST /crawls/${crawlId}/pause failed: ${r.status}`);
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crawls"] });
    },
  });
}

export interface ResumeCrawlResponse {
  ok: boolean;
  paused_crawl_id: number;
  new_crawl_id: number;
}

export function useResumeCrawl(crawlId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<ResumeCrawlResponse> => {
      const r = await fetch(`/api/crawls/${crawlId}/resume`, {
        method: "POST",
        credentials: "same-origin",
      });
      if (!r.ok) {
        const body = await r.text().catch(() => "");
        throw new Error(`POST /crawls/${crawlId}/resume failed: ${r.status} ${body}`);
      }
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crawls"] });
    },
  });
}

// ─── SSH access (per-property) ────────────────────────────────────

export interface SSHConnectionStatus {
  connected: boolean;
  host: string | null;
  user: string | null;
  port: number | null;
  public_key: string | null;
  fingerprint: string | null;
  install_snippet: string | null;
  connected_at: string | null;
  last_test_ok: boolean | null;
}

export interface SSHStartRequest {
  property_id: number;
  host: string;
  user: string;
  port?: number;
}

export interface SSHStartResponse {
  public_key: string;
  fingerprint: string;
  install_snippet: string;
  host: string;
  user: string;
  port: number;
}

export interface SSHTestResponse {
  ok: boolean;
  error: string | null;
  whoami: string | null;
  uname: string | null;
  chosen_log_path: string | null;
  access_logs_dir: string | null;
  access_log_for_domain: string | null;
  logs_dir: string | null;
  shared_domlogs: string | null;
}

export function useSSHStatus(propertyId: number | null) {
  return useQuery({
    queryKey: ["ssh", "status", propertyId],
    queryFn: ({ signal }) =>
      apiGet<SSHConnectionStatus>(`/ssh/status?property_id=${propertyId}`, signal),
    enabled: propertyId !== null && propertyId > 0,
    staleTime: 30_000,
  });
}

export function useStartSSH() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: SSHStartRequest) => {
      const r = await fetch("/api/ssh/start", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        let detail = `${r.status}`;
        try { detail = (await r.json()).detail || detail; } catch { /* ignore */ }
        throw new Error(`POST /ssh/start failed: ${detail}`);
      }
      return r.json() as Promise<SSHStartResponse>;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["ssh", "status", vars.property_id] });
    },
  });
}

export function useTestSSH() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (propertyId: number) => {
      const r = await fetch("/api/ssh/test", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ property_id: propertyId }),
      });
      if (!r.ok) {
        let detail = `${r.status}`;
        try { detail = (await r.json()).detail || detail; } catch { /* ignore */ }
        throw new Error(`POST /ssh/test failed: ${detail}`);
      }
      return r.json() as Promise<SSHTestResponse>;
    },
    onSuccess: (_d, propertyId) => {
      qc.invalidateQueries({ queryKey: ["ssh", "status", propertyId] });
      qc.invalidateQueries({ queryKey: ["properties"] });
    },
  });
}

export function useDisconnectSSH() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (propertyId: number) => {
      const r = await fetch(`/api/ssh?property_id=${propertyId}`, {
        method: "DELETE",
        credentials: "same-origin",
      });
      if (!r.ok) throw new Error(`DELETE /ssh failed: ${r.status}`);
      return r.json();
    },
    onSuccess: (_d, propertyId) => {
      qc.invalidateQueries({ queryKey: ["ssh", "status", propertyId] });
      qc.invalidateQueries({ queryKey: ["properties"] });
    },
  });
}


// ─── Sprint E: crawl audit substrate ──────────────────────────────

export interface CrawlOverviewOut {
  crawl_id: number;
  overview: Record<string, Record<string, string>> | null;
  audit_ingested_at: string | null;
  counts: Record<string, number>;
}

export function useCrawlOverview(crawlId: number | null) {
  return useQuery({
    queryKey: ["crawl-overview", crawlId],
    queryFn: ({ signal }) =>
      apiGet<CrawlOverviewOut>(`/crawls/${crawlId}/overview`, signal),
    enabled: crawlId !== null && crawlId > 0,
    staleTime: 30_000,
  });
}

export interface CrawlIssue {
  id: number;
  issue_name: string;
  issue_slug: string | null;
  issue_type: string;
  priority: string | null;
  urls_count: number | null;
  pct_of_total: number | null;
  description: string | null;
  how_to_fix: string | null;
  url: string | null;
  raw: Record<string, unknown> | null;
}

export interface CrawlIssuesFilters {
  issue_type?: string;
  priority?: string;
  search?: string;
  issue_slug?: string;  // drill-down: when set, returns URL-level rows for one issue
  sort?: string;
  sort_dir?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

export interface CrawlIssuesOut {
  items: CrawlIssue[];
  total: number;
  by_type: Record<string, number>;
  by_priority: Record<string, number>;
}

export function useCrawlIssues(crawlId: number | null, filters: CrawlIssuesFilters) {
  const qs = new URLSearchParams();
  if (filters.issue_type) qs.set("issue_type", filters.issue_type);
  if (filters.priority) qs.set("priority", filters.priority);
  if (filters.search) qs.set("search", filters.search);
  if (filters.issue_slug) qs.set("issue_slug", filters.issue_slug);
  if (filters.sort) qs.set("sort", filters.sort);
  if (filters.sort_dir) qs.set("sort_dir", filters.sort_dir);
  qs.set("limit", String(filters.limit ?? 200));
  qs.set("offset", String(filters.offset ?? 0));
  return useQuery({
    queryKey: ["crawl-issues", crawlId, qs.toString()],
    queryFn: ({ signal }) =>
      apiGet<CrawlIssuesOut>(`/crawls/${crawlId}/issues?${qs.toString()}`, signal),
    enabled: crawlId !== null && crawlId > 0,
    staleTime: 30_000,
  });
}

export interface CrawlImage {
  id: number;
  image_url: string;
  source_url: string | null;
  alt_text: string | null;
  has_alt: boolean | null;
  status_code: number | null;
  size_bytes: number | null;
}

export interface CrawlImagesFilters {
  missing_alt?: boolean;
  only_broken?: boolean;
  min_size_kb?: number;
  search?: string;
  sort?: string;
  sort_dir?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

export interface CrawlImagesOut {
  items: CrawlImage[];
  total: number;
}

export function useCrawlImages(crawlId: number | null, filters: CrawlImagesFilters) {
  const qs = new URLSearchParams();
  if (filters.missing_alt !== undefined) qs.set("missing_alt", String(filters.missing_alt));
  if (filters.only_broken) qs.set("only_broken", "true");
  if (filters.min_size_kb !== undefined) qs.set("min_size_kb", String(filters.min_size_kb));
  if (filters.search) qs.set("search", filters.search);
  if (filters.sort) qs.set("sort", filters.sort);
  if (filters.sort_dir) qs.set("sort_dir", filters.sort_dir);
  qs.set("limit", String(filters.limit ?? 200));
  qs.set("offset", String(filters.offset ?? 0));
  return useQuery({
    queryKey: ["crawl-images", crawlId, qs.toString()],
    queryFn: ({ signal }) =>
      apiGet<CrawlImagesOut>(`/crawls/${crawlId}/images?${qs.toString()}`, signal),
    enabled: crawlId !== null && crawlId > 0,
    staleTime: 30_000,
  });
}

export interface CrawlInlink {
  id: number;
  source_url: string;
  target_url: string;
  anchor: string | null;
  alt_text: string | null;
  status_code: number | null;
  follow: string | null;
  link_type: string | null;
}

export interface CrawlInlinksFilters {
  target_url?: string;
  source_url?: string;
  only_broken?: boolean;
  only_nofollow?: boolean;
  // 'Hyperlink' (default), 'Image', 'CSS', 'JavaScript',
  // 'HTML Hreflang', 'HTML Canonical', 'Iframe', 'HTML Rel Next',
  // 'HTTP Redirect', or 'all' to show every type. Default in the
  // backend is 'Hyperlink' so the noise (Image/CSS/JS refs) doesn't
  // dominate the SEO inlink view. Pass 'all' explicitly when debugging.
  link_type?: string;
  // Hide self-links (source_url == target_url). Default true on the
  // backend — homepage→homepage etc. is just navigation noise.
  exclude_self?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface CrawlInlinksOut {
  items: CrawlInlink[];
  total: number;
}

export function useCrawlInlinks(crawlId: number | null, filters: CrawlInlinksFilters) {
  const qs = new URLSearchParams();
  if (filters.target_url) qs.set("target_url", filters.target_url);
  if (filters.source_url) qs.set("source_url", filters.source_url);
  if (filters.only_broken) qs.set("only_broken", "true");
  if (filters.only_nofollow) qs.set("only_nofollow", "true");
  if (filters.link_type) qs.set("link_type", filters.link_type);
  if (filters.exclude_self === false) qs.set("exclude_self", "false");
  if (filters.search) qs.set("search", filters.search);
  qs.set("limit", String(filters.limit ?? 200));
  qs.set("offset", String(filters.offset ?? 0));
  return useQuery({
    queryKey: ["crawl-inlinks", crawlId, qs.toString()],
    queryFn: ({ signal }) =>
      apiGet<CrawlInlinksOut>(`/crawls/${crawlId}/inlinks?${qs.toString()}`, signal),
    enabled: crawlId !== null && crawlId > 0,
    staleTime: 30_000,
  });
}

export interface CrawlRedirect {
  id: number;
  source_url: string;
  final_url: string | null;
  hops: number | null;
  final_status: number | null;
  has_loop: boolean;
}

export interface CrawlRedirectsFilters {
  only_loops?: boolean;
  min_hops?: number;
  limit?: number;
  offset?: number;
}

export interface CrawlRedirectsOut {
  items: CrawlRedirect[];
  total: number;
  long_chain_count: number;
  loop_count: number;
}

// Generic substrate slice (canonicals/hreflang/schema/mobile/security/orphans)
export type SubstrateKind =
  "canonicals" | "hreflang" | "schema" | "mobile" | "security" | "orphans";

export interface CrawlSubstrateRow {
  id: number;
  url: string | null;
  // Canonicals + per-URL fields
  canonical: string | null;
  http_canonical: string | null;
  occurrences: number | null;
  indexability: string | null;
  indexability_status: string | null;
  viewport: string | null;
  errors: number | null;
  warnings: number | null;
  types: string | null;
  // Security
  security_kind: string | null;
  source_url: string | null;
  target_url: string | null;
  // Orphan pages
  source: string | null;
  inlinks: number | null;
  raw: Record<string, unknown> | null;
}

export interface CrawlSubstrateFilters {
  search?: string;
  only_missing?: boolean;
  only_errors?: boolean;
  security_kind?: string;
  limit?: number;
  offset?: number;
}

export interface CrawlSubstrateOut {
  items: CrawlSubstrateRow[];
  total: number;
  kind: string;
  by_filter: Record<string, number>;
}

export function useCrawlSubstrate(crawlId: number | null, kind: SubstrateKind, filters: CrawlSubstrateFilters) {
  const qs = new URLSearchParams();
  if (filters.search) qs.set("search", filters.search);
  if (filters.only_missing !== undefined) qs.set("only_missing", String(filters.only_missing));
  if (filters.only_errors !== undefined) qs.set("only_errors", String(filters.only_errors));
  if (filters.security_kind) qs.set("security_kind", filters.security_kind);
  qs.set("limit", String(filters.limit ?? 200));
  qs.set("offset", String(filters.offset ?? 0));
  return useQuery({
    queryKey: ["crawl-substrate", crawlId, kind, qs.toString()],
    queryFn: ({ signal }) =>
      apiGet<CrawlSubstrateOut>(`/crawls/${crawlId}/substrate/${kind}?${qs.toString()}`, signal),
    enabled: crawlId !== null && crawlId > 0,
    staleTime: 30_000,
  });
}


export function useCrawlRedirects(crawlId: number | null, filters: CrawlRedirectsFilters) {
  const qs = new URLSearchParams();
  if (filters.only_loops) qs.set("only_loops", "true");
  if (filters.min_hops !== undefined) qs.set("min_hops", String(filters.min_hops));
  qs.set("limit", String(filters.limit ?? 200));
  qs.set("offset", String(filters.offset ?? 0));
  return useQuery({
    queryKey: ["crawl-redirects", crawlId, qs.toString()],
    queryFn: ({ signal }) =>
      apiGet<CrawlRedirectsOut>(`/crawls/${crawlId}/redirects?${qs.toString()}`, signal),
    enabled: crawlId !== null && crawlId > 0,
    staleTime: 30_000,
  });
}
